/**
 * Pericope study handler — whole-passage study, deterministic DB lookup.
 *
 * Mirrors verse-study-handler but scoped to the pericope containing the
 * reference: the server canonicalizes the anchor to the pericope's first
 * verse and returns In Context data only (scope: 'pericope'). Renderers
 * branch on that scope to skip per-verse analysis.
 */

import { runInAction } from 'mobx';
import { fetchVerseStudy } from '@api/verse-study';
import type { ChatState } from './chat-state';

/** Add a pericope study message */
export async function addPericopeStudy(state: ChatState, reference: string): Promise<void> {
  state.clearHistory();
  // clearHistory readies the composer for a fresh chat; a programmatic
  // study flow keeps it hidden (same rule as recalled commentary).
  state.isComposerVisible = false;
  const loadingId = state.nextId();
  state.messages.push({
    id: loadingId,
    role: 'assistant',
    content: '',
    timestamp: new Date().toISOString(),
    isLoading: true,
  });
  state.isLoading = true;

  try {
    const response = await fetchVerseStudy(reference, 'pericope');
    runInAction(() => {
      const idx = state.messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        const p = response.pericope;
        const rangeLabel = p
          ? `${p.book} ${p.chapter}:${p.startVerse}-${p.endVerse} — ${p.title}`
          : reference;
        state.messages[idx] = {
          id: loadingId,
          role: 'assistant',
          content: rangeLabel,
          timestamp: new Date().toISOString(),
          scriptureResponse: response,
          commentaryState: 'not-requested',
          intent: 'pericope_study',
          originalQuery: `Study the passage ${rangeLabel}`,
        };
      }
      state.isLoading = false;
      state.persistLocal();
    });
  } catch {
    runInAction(() => {
      const idx = state.messages.findIndex((m) => m.id === loadingId);
      if (idx !== -1) {
        state.messages[idx] = {
          id: loadingId,
          role: 'assistant',
          content: 'Unable to load the passage study. Please try again later.',
          timestamp: new Date().toISOString(),
          isError: true,
        };
      }
      state.isLoading = false;
      state.persistLocal();
    });
  }
}
